import React, { useEffect, useState, useRef } from 'react';
import { XMarkIcon, DocumentDuplicateIcon, PaperAirplaneIcon, CheckCircleIcon, XCircleIcon, ArrowDownTrayIcon, PrinterIcon, PencilIcon } from '@heroicons/react/24/outline';
import { Quote, quotesAPI, stockAlertsAPI, InventoryAvailabilityStatus } from '../../services/api';
import { Button, Badge, Spinner } from '../ui';
import QuoteSendModal from './QuoteSendModal';

interface QuoteDetailModalProps {
  quoteId: number;
  onClose: () => void;
  onRefresh?: () => void;
  onEdit?: (quote: Quote) => void;
}

const normalizeUnit = (value?: string | null) => value?.trim().toLowerCase() || '';

const getStockKey = (productId?: number, unit?: string | null) => {
  if (!productId) return '';
  return `${productId}:${normalizeUnit(unit)}`;
};

const QuoteDetailModal: React.FC<QuoteDetailModalProps> = ({ quoteId, onClose, onRefresh, onEdit }) => {
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [stockDetails, setStockDetails] = useState<Record<string, {
    productId: number;
    productName: string;
    sku: string;
    isInventoryLinked: boolean;
    requiredQuantity: number;
    availableQuantity: number;
    status: InventoryAvailabilityStatus;
    requestedUnit?: string | null;
    inventoryUnit?: string | null;
    unitMismatch?: boolean;
    inventoryItems: Array<{ id: number; quantity: number; batchNumber: string | null; location: string }>;
  }>>({});
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchQuote();
  }, [quoteId]);

  useEffect(() => {
    if (!quote?.items?.length) {
      setStockDetails({});
      return;
    }

    const quantitiesByProductAndUnit = new Map<string, { productId: number; requiredQuantity: number; unit?: string }>();
    quote.items.forEach((item) => {
      if (!item.productId) return;
      const key = getStockKey(item.productId, item.unit);
      const existing = quantitiesByProductAndUnit.get(key);
      quantitiesByProductAndUnit.set(key, {
        productId: item.productId,
        requiredQuantity: (existing?.requiredQuantity || 0) + Number(item.quantity || 0),
        unit: item.unit,
      });
    });

    const availabilityItems = Array.from(quantitiesByProductAndUnit.values())
      .filter((item) => item.requiredQuantity > 0);

    if (availabilityItems.length === 0) {
      setStockDetails({});
      return;
    }

    const checkStock = async () => {
      try {
        const result = await stockAlertsAPI.checkAvailability(availabilityItems);
        setStockDetails(
          result.details.reduce((acc, detail) => {
            acc[getStockKey(detail.productId, detail.requestedUnit)] = detail;
            return acc;
          }, {} as typeof stockDetails),
        );
      } catch (err) {
        console.error('Failed to check quote inventory:', err);
      }
    };

    checkStock();
  }, [quote]);

  const fetchQuote = async () => {
    try {
      setLoading(true);
      const data = await quotesAPI.getById(quoteId);
      setQuote(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load quote');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  const getStatusVariant = (status: string): 'primary' | 'success' | 'warning' | 'error' | 'neutral' | 'secondary' => {
    const variants: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'neutral' | 'secondary'> = {
      DRAFT: 'neutral',
      SENT: 'primary',
      ACCEPTED: 'success',
      REJECTED: 'error',
      EXPIRED: 'warning',
    };
    return variants[status] || 'neutral';
  };

  const getStatusIcon = (status: string) => {
    const icons: Record<string, any> = {
      DRAFT: DocumentDuplicateIcon,
      SENT: PaperAirplaneIcon,
      ACCEPTED: CheckCircleIcon,
      REJECTED: XCircleIcon,
      EXPIRED: XCircleIcon,
    };
    return icons[status] || DocumentDuplicateIcon;
  };

  const getStockTone = (status: InventoryAvailabilityStatus) => {
    switch (status) {
      case 'AVAILABLE':
        return 'border-success-200 bg-success-50 text-success-700';
      case 'PARTIAL':
        return 'border-warning-200 bg-warning-50 text-warning-700';
      case 'OUT_OF_STOCK':
        return 'border-error-200 bg-error-50 text-error-700';
      case 'NOT_TRACKED':
      default:
        return 'border-neutral-200 bg-neutral-50 text-neutral-600';
    }
  };

  const getStockLabel = (status: InventoryAvailabilityStatus) => {
    switch (status) {
      case 'AVAILABLE':
        return 'Available';
      case 'PARTIAL':
        return 'Partial stock';
      case 'OUT_OF_STOCK':
        return 'Out of stock';
      case 'NOT_TRACKED':
      default:
        return 'Not inventory-tracked';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-neutral-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-xl">
          <Spinner size="lg" className="mx-auto" />
          <p className="mt-4 text-neutral-600">Loading quote...</p>
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="fixed inset-0 bg-neutral-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-xl max-w-md">
          <p className="text-error-600 mb-4">{error || 'Quote not found'}</p>
          <Button variant="secondary" onClick={onClose} fullWidth>
            Close
          </Button>
        </div>
      </div>
    );
  }

  const displayClient = quote.client || quote.deal?.client;

  return (
    <>
      {/* Print-specific styles */}
      <style>{`
        #quote-print-content {
          position: fixed;
          left: -9999px;
          top: 0;
        }

        @media print {
          @page {
            size: A4;
            margin: 20mm;
          }

          /* Hide browser-added headers and footers */
          @page {
            margin-top: 20mm;
            margin-bottom: 20mm;
          }

          body * {
            visibility: hidden;
          }

          #quote-print-content,
          #quote-print-content * {
            visibility: visible;
          }

          #quote-print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white;
            padding: 0;
          }

          .no-print {
            display: none !important;
          }

          .print-page-break {
            page-break-before: always;
          }

          .print-avoid-break {
            page-break-inside: avoid;
          }

          /* Remove default browser print headers/footers */
          html, body {
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>

      <div className="fixed inset-0 bg-neutral-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 no-print">
        <div className="relative top-10 mx-auto p-6 border max-w-5xl shadow-2xl rounded-xl bg-white mb-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-neutral-200">
          <div>
            <h3 className="text-2xl font-bold text-neutral-900">{quote.title}</h3>
            <p className="text-sm text-neutral-500 mt-1">Quote #{quote.quoteNumber}</p>
          </div>
          <div className="flex items-center space-x-3">
            <Badge variant={getStatusVariant(quote.status)} size="lg">
              {React.createElement(getStatusIcon(quote.status), { className: 'h-4 w-4 mr-1' })}
              {quote.status}
            </Badge>
            <button
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600 transition-colors no-print"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Deal & Client Info */}
        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-neutral-600">Client</p>
              <p className="font-semibold text-neutral-900">{displayClient?.name || 'No client'}</p>
            </div>
            <div>
              <p className="text-sm text-neutral-600">Deal</p>
              <p className="font-semibold text-neutral-900">{quote.deal?.description || 'Standalone quote'}</p>
            </div>
            <div>
              <p className="text-sm text-neutral-600">Valid Until</p>
              <p className="font-semibold text-neutral-900">
                {quote.validUntil ? new Date(quote.validUntil).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-neutral-600">Created By</p>
              <p className="font-semibold text-neutral-900">
                {quote.createdBy?.firstName} {quote.createdBy?.lastName}
              </p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        {quote.introduction && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-neutral-700 mb-2">Introduction</h4>
            <p className="text-neutral-600 whitespace-pre-wrap">{quote.introduction}</p>
          </div>
        )}

        {Object.values(stockDetails).length > 0 && (
          <div className="mb-6 space-y-2">
            <h4 className="text-sm font-semibold text-neutral-700">Inventory</h4>
            {Object.values(stockDetails).map((detail) => (
              <div key={getStockKey(detail.productId, detail.requestedUnit)} className={`rounded-lg border px-4 py-3 text-sm ${getStockTone(detail.status)}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">
                    {detail.productName}: {detail.unitMismatch
                      ? `Inventory not checked for ${detail.requestedUnit || 'selected unit'}`
                      : getStockLabel(detail.status)}
                  </span>
                  {detail.unitMismatch ? (
                    <span>Inventory unit: {detail.inventoryUnit || 'not set'}</span>
                  ) : detail.isInventoryLinked && (
                    <span>Available {detail.availableQuantity} / Required {detail.requiredQuantity}</span>
                  )}
                </div>
                {detail.inventoryItems.length > 0 && (
                  <div className="mt-1 text-xs opacity-90">
                    Lots: {detail.inventoryItems.map((item) => item.batchNumber).filter(Boolean).slice(0, 4).join(', ') || '-'}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Items Table */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-neutral-700 mb-3">Quote Items</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Item</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">SKU</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">Qty</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">Unit Price</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">Discount</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">Subtotal</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {quote.items?.map((item) => (
                  <tr key={item.id} className={item.isOptional ? 'bg-primary-50' : ''}>
                    <td className="px-4 py-3 text-sm text-neutral-900">
                      {item.name}
                      {item.isOptional && (
                        <span className="ml-2 text-xs text-primary-600">(Optional)</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-500">{item.sku || '-'}</td>
                    <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                      {item.quantity} {item.unit}
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                      ${Number(item.unitPrice).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                      {Number(item.discountPercent) > 0 && `${Number(item.discountPercent)}% `}
                      ${Number(item.discountAmount).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-neutral-900 text-right">
                      ${Number(item.subtotal).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals */}
        <div className="bg-neutral-50 rounded-lg p-4 mb-6">
          <div className="max-w-sm ml-auto space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Subtotal:</span>
              <span className="font-semibold">${Number(quote.subtotal).toFixed(2)}</span>
            </div>
            {Number(quote.discountPercent) > 0 && (
              <div className="flex justify-between text-sm text-success-600">
                <span>Discount ({Number(quote.discountPercent)}%):</span>
                <span>-${Number(quote.discountAmount).toFixed(2)}</span>
              </div>
            )}
            {Number(quote.taxPercent) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600">Tax ({Number(quote.taxPercent)}%):</span>
                <span className="font-semibold">${Number(quote.taxAmount).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold text-primary-600 pt-2 border-t border-neutral-200">
              <span>Total:</span>
              <span>${Number(quote.total).toFixed(2)} {quote.currency}</span>
            </div>
          </div>
        </div>

        {/* Terms & Notes */}
        {quote.terms && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-neutral-700 mb-2">Terms & Conditions</h4>
            <p className="text-sm text-neutral-600 whitespace-pre-wrap">{quote.terms}</p>
          </div>
        )}

        {quote.notes && (
          <div className="mb-6 bg-warning-50 border border-warning-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-warning-800 mb-2">Internal Notes</h4>
            <p className="text-sm text-warning-700 whitespace-pre-wrap">{quote.notes}</p>
          </div>
        )}

        {/* PDF Export Tip */}
        <div className="mb-4 bg-primary-50 border border-primary-200 rounded-lg p-3 no-print">
          <p className="text-xs text-primary-800">
            💡 <strong>PDF Export Tip:</strong> In the print dialog, click "More settings" and turn OFF "Headers and footers" to remove the URL from your PDF.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center pt-4 border-t border-neutral-200 no-print">
          <div className="text-sm text-neutral-500">
            {quote.viewCount > 0 && (
              <span>Viewed {quote.viewCount} time{quote.viewCount !== 1 ? 's' : ''}</span>
            )}
          </div>
          <div className="flex space-x-3">
            {quote.status !== 'ACCEPTED' && onEdit && (
              <Button variant="outline" onClick={() => onEdit(quote)} leftIcon={<PencilIcon className="h-4 w-4" />}>
                Edit
              </Button>
            )}
            <Button variant="outline" onClick={handlePrint} leftIcon={<PrinterIcon className="h-4 w-4" />}>
              Print
            </Button>
            <Button variant="success" onClick={handleDownloadPDF} leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}>
              Export PDF
            </Button>
            {quote.status === 'DRAFT' && (
              <Button variant="primary" onClick={() => setShowSendModal(true)} leftIcon={<PaperAirplaneIcon className="h-4 w-4" />}>
                Send Quote
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>

      {/* Hidden Print-Only Content - Elegant Quote Design */}
      <div id="quote-print-content">
        {/* Elegant Header */}
        <div style={{ borderBottom: '3px solid #7C3AED', paddingBottom: '20px', marginBottom: '30px' }}>
          <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#1F2937', marginBottom: '8px' }}>
            QUOTATION
          </h1>
          <p style={{ fontSize: '18px', color: '#6B7280' }}>
            Quote #{quote.quoteNumber}
          </p>
        </div>

        {/* Company & Client Info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '40px' }}>
          <div>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              From
            </h3>
            <p style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>
              {quote.createdBy?.firstName} {quote.createdBy?.lastName}
            </p>
            <p style={{ fontSize: '14px', color: '#6B7280' }}>
              {quote.company?.name}
            </p>
          </div>
          <div>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              To
            </h3>
            <p style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>
              {displayClient?.name || 'No client'}
            </p>
            {displayClient?.email && (
              <p style={{ fontSize: '14px', color: '#6B7280' }}>
                {displayClient.email}
              </p>
            )}
          </div>
        </div>

        {/* Quote Details */}
        <div style={{ backgroundColor: '#F9FAFB', padding: '20px', borderRadius: '8px', marginBottom: '30px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
            <div>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Quote Date</p>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>
                {new Date(quote.createdAt).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Valid Until</p>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>
                {quote.validUntil ? new Date(quote.validUntil).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Currency</p>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>
                {quote.currency}
              </p>
            </div>
          </div>
        </div>

        {/* Quote Title & Introduction */}
        {quote.title && (
          <div style={{ marginBottom: '30px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', marginBottom: '12px' }}>
              {quote.title}
            </h2>
          </div>
        )}

        {quote.introduction && (
          <div style={{ marginBottom: '30px', lineHeight: '1.6' }}>
            <p style={{ fontSize: '14px', color: '#4B5563', whiteSpace: 'pre-wrap' }}>
              {quote.introduction}
            </p>
          </div>
        )}

        {/* Items Table */}
        <div style={{ marginBottom: '30px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#7C3AED', color: 'white' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Item
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Quantity
                </th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Unit Price
                </th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Discount
                </th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {quote.items?.map((item, index) => (
                <tr key={item.id} style={{ borderBottom: '1px solid #E5E7EB', backgroundColor: index % 2 === 0 ? 'white' : '#F9FAFB' }}>
                  <td style={{ padding: '12px' }}>
                    <p style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937', marginBottom: '2px' }}>
                      {item.name}
                    </p>
                    {item.sku && (
                      <p style={{ fontSize: '12px', color: '#6B7280' }}>
                        SKU: {item.sku}
                      </p>
                    )}
                    {item.description && (
                      <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                        {item.description}
                      </p>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px', color: '#1F2937' }}>
                    {item.quantity} {item.unit}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px', color: '#1F2937' }}>
                    ${Number(item.unitPrice).toFixed(2)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px', color: '#10B981' }}>
                    {Number(item.discountPercent) > 0 && `${Number(item.discountPercent)}%`}
                    {Number(item.discountAmount) > 0 && ` -$${Number(item.discountAmount).toFixed(2)}`}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>
                    ${Number(item.subtotal).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div style={{ marginBottom: '40px', display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: '300px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #E5E7EB' }}>
              <span style={{ fontSize: '14px', color: '#6B7280' }}>Subtotal:</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>
                ${Number(quote.subtotal).toFixed(2)}
              </span>
            </div>

            {Number(quote.discountPercent) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #E5E7EB' }}>
                <span style={{ fontSize: '14px', color: '#10B981' }}>
                  Discount ({Number(quote.discountPercent)}%):
                </span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#10B981' }}>
                  -${Number(quote.discountAmount).toFixed(2)}
                </span>
              </div>
            )}

            {Number(quote.taxPercent) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #E5E7EB' }}>
                <span style={{ fontSize: '14px', color: '#6B7280' }}>
                  Tax ({Number(quote.taxPercent)}%):
                </span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>
                  ${Number(quote.taxAmount).toFixed(2)}
                </span>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', backgroundColor: '#7C3AED', color: 'white', marginTop: '8px', paddingLeft: '12px', paddingRight: '12px', borderRadius: '6px' }}>
              <span style={{ fontSize: '18px', fontWeight: '700' }}>TOTAL:</span>
              <span style={{ fontSize: '18px', fontWeight: '700' }}>
                {quote.currency} ${Number(quote.total).toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Terms & Conditions */}
        {quote.terms && (
          <div style={{ marginBottom: '30px', pageBreakInside: 'avoid' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '12px', borderBottom: '2px solid #E5E7EB', paddingBottom: '8px' }}>
              Terms & Conditions
            </h3>
            <p style={{ fontSize: '13px', color: '#4B5563', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
              {quote.terms}
            </p>
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: '60px', paddingTop: '20px', borderTop: '2px solid #E5E7EB', textAlign: 'center' }}>
          <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}>
            Thank you for your business!
          </p>
          <p style={{ fontSize: '11px', color: '#9CA3AF' }}>
            This quote is valid until {quote.validUntil ? new Date(quote.validUntil).toLocaleDateString() : 'the date specified above'}
          </p>
        </div>
      </div>

      <QuoteSendModal
        quote={showSendModal ? quote : null}
        onClose={() => setShowSendModal(false)}
        onSent={async () => {
          await fetchQuote();
          onRefresh?.();
        }}
      />
    </>
  );
};

export default QuoteDetailModal;
